import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Switch,
  Typography
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useDesignerUI } from './DesignerUIContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PublishIcon from '@mui/icons-material/Publish';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import GestureIcon from '@mui/icons-material/Gesture';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import ShortTextIcon from '@mui/icons-material/ShortText';
import SubjectIcon from '@mui/icons-material/Subject';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import NumbersIcon from '@mui/icons-material/Numbers';
import TitleIcon from '@mui/icons-material/Title';
import TodayIcon from '@mui/icons-material/Today';
import ArticleIcon from '@mui/icons-material/Article';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDropzone } from 'react-dropzone';
import { Document, Page, pdfjs } from 'react-pdf';
import { Rnd } from 'react-rnd';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  getTemplate as getFormsTemplate,
  updateTemplate as updateFormsTemplate
} from '../../services/formsApi';
import SignatureCapture from './SignatureCapture';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const FIELD_PRESETS = [
  {
    type: 'title',
    label: 'Form Title',
    required: false,
    description: 'Display a large heading at the top of the form.',
    icon: TitleIcon,
    modes: ['builder', 'pdf'],
    defaultConfig: {
      headingText: 'Form Title',
      subtitle: '',
      align: 'center'
    }
  },
  {
    type: 'static_text',
    label: 'Text Block',
    required: false,
    description: 'Add narrative text, instructions, or policy language to the form.',
    icon: ArticleIcon,
    modes: ['builder', 'pdf'],
    defaultConfig: {
      content: 'Describe the purpose of this form here. You can add multiple paragraphs to outline expectations or provide context.',
      align: 'left'
    }
  },
  {
    type: 'sent_date',
    label: 'Send Date',
    required: false,
    description: 'Placeholder that auto-fills with the send date.',
    icon: TodayIcon,
    modes: ['builder', 'pdf'],
    defaultConfig: {
      dateFormat: 'MMMM d, yyyy',
      helperText: 'Auto-populated when the template is sent'
    }
  },
  {
    type: 'signature',
    label: 'Signature',
    required: true,
    description: 'Collect a signer signature box.',
    icon: GestureIcon,
    modes: ['pdf', 'builder']
  },
  {
    type: 'initials',
    label: 'Initials',
    required: true,
    description: 'Capture quick initials on each page.',
    icon: SpellcheckIcon,
    modes: ['pdf', 'builder']
  },
  {
    type: 'text',
    label: 'Short Answer',
    required: false,
    description: 'Single-line response field.',
    icon: ShortTextIcon,
    modes: ['pdf', 'builder'],
    placeholder: 'Enter response'
  },
  {
    type: 'textarea',
    label: 'Paragraph',
    required: false,
    description: 'Multi-line response area.',
    icon: SubjectIcon,
    modes: ['builder'],
    placeholder: 'Add details here'
  },
  {
    type: 'checkbox',
    label: 'Checkbox Group',
    required: false,
    description: 'Allow multiple selections.',
    icon: CheckBoxIcon,
    modes: ['builder'],
    options: ['Option 1', 'Option 2']
  },
  {
    type: 'select',
    label: 'Dropdown',
    required: false,
    description: 'Single choice dropdown menu.',
    icon: ListAltIcon,
    modes: ['builder'],
    options: ['Option 1', 'Option 2']
  },
  {
    type: 'date',
    label: 'Date',
    required: true,
    description: 'Collect a calendar date.',
    icon: EventAvailableIcon,
    modes: ['pdf', 'builder']
  },
  {
    type: 'email',
    label: 'Email',
    required: false,
    description: 'Validate email input.',
    icon: EmailIcon,
    modes: ['builder'],
    placeholder: 'name@example.com'
  },
  {
    type: 'phone',
    label: 'Phone Number',
    required: false,
    description: 'Capture a contact phone.',
    icon: PhoneIcon,
    modes: ['builder'],
    placeholder: '(555) 123-4567'
  },
  {
    type: 'number',
    label: 'Number',
    required: false,
    description: 'Collect numeric values.',
    icon: NumbersIcon,
    modes: ['builder'],
    placeholder: 'Enter a number'
  }
];

const PLACEHOLDER_FIELD_TYPES = new Set(['text', 'textarea', 'email', 'phone', 'number']);
const OPTION_FIELD_TYPES = new Set(['checkbox', 'select']);
const STATIC_FIELD_TYPES = new Set(['title', 'sent_date', 'static_text']);

const defaultPosition = (page = 1) => ({
  page,
  x: 0.2,
  y: 0.2,
  width: 0.25,
  height: 0.08
});

const inferTemplateType = template => {
  if (!template) {
    return 'pdf';
  }
  const attachmentsCount = template.schema?.attachments?.length ?? 0;
  if (template.template_type === 'pdf' && attachmentsCount === 0) {
    return 'builder';
  }
  return template.template_type || 'pdf';
};

const createStepHelper = (templateType, context) => {
  const { activeAttachment, fields, hasUnsavedChanges, template } = context;
  if (templateType === 'builder') {
    const configuredCount = fields.filter(field => field.label && field.field_key).length;
    const allConfigured = fields.length > 0 && configuredCount === fields.length;
    const definitions = [
      {
        label: 'Add Fields',
        helper: fields.length > 0 ? `${fields.length} field${fields.length === 1 ? '' : 's'} added` : 'Drag or click fields from the palette.'
      },
      {
        label: 'Configure',
        helper: allConfigured ? 'All fields have labels and keys.' : 'Set labels, keys, and required flags.'
      },
      {
        label: 'Review & Save',
        helper: hasUnsavedChanges ? 'Click Save Layout to capture changes.' : 'Layout saved and ready.'
      },
      {
        label: 'Publish',
        helper: template?.status === 'published' ? 'Template is live for sending.' : 'Publish when everything looks good.'
      }
    ];

    const activeStep = fields.length === 0
      ? 0
      : !allConfigured
        ? 1
        : hasUnsavedChanges
          ? 2
          : template?.status === 'published'
            ? 3
            : 2;

    const isStepCompleted = stepIndex => {
      switch (stepIndex) {
        case 0:
          return fields.length > 0;
        case 1:
          return allConfigured;
        case 2:
          return fields.length > 0 && !hasUnsavedChanges;
        case 3:
          return template?.status === 'published';
        default:
          return false;
      }
    };

    return { definitions, activeStep, isStepCompleted };
  }

  const definitions = [
    {
      label: 'Upload PDF',
      helper: activeAttachment ? activeAttachment.file_name : 'Drop a template or click to upload.'
    },
    {
      label: 'Place Fields',
      helper:
        fields.length > 0
          ? `${fields.length} field${fields.length === 1 ? '' : 's'} on the canvas`
          : 'Add signer fields from the palette.'
    },
    {
      label: 'Review & Save',
      helper: hasUnsavedChanges ? 'Remember to save your layout.' : 'Layout saved and ready.'
    },
    {
      label: 'Publish',
      helper: template?.status === 'published' ? 'Template is live for signing.' : 'Publish when everything looks good.'
    }
  ];

  const activeStep = !activeAttachment
    ? 0
    : fields.length === 0
      ? 1
      : hasUnsavedChanges
        ? 2
        : template?.status === 'published'
          ? 3
          : 2;

  const isStepCompleted = stepIndex => {
    switch (stepIndex) {
      case 0:
        return Boolean(activeAttachment);
      case 1:
        return fields.length > 0;
      case 2:
        return Boolean(activeAttachment) && fields.length > 0 && !hasUnsavedChanges;
      case 3:
        return template?.status === 'published';
      default:
        return false;
    }
  };

  return { definitions, activeStep, isStepCompleted };
};

const createQuickStartItems = (templateType, context) => {
  const { activeAttachment, fields, hasUnsavedChanges, template } = context;

  if (templateType === 'builder') {
    const allConfigured = fields.length > 0 && fields.every(field => field.label && field.field_key);
    return [
      {
        label: 'Add form fields',
        description: 'Drag or click fields from the palette to build your layout.',
        completed: fields.length > 0
      },
      {
        label: 'Configure each field',
        description: 'Update labels, required status, and options in the Field Details panel.',
        completed: allConfigured
      },
      {
        label: 'Save the layout',
        description: 'Click Save Layout after editing to capture audit-ready settings.',
        completed: fields.length > 0 && !hasUnsavedChanges
      },
      {
        label: 'Publish the template',
        description: 'Publish when your form is ready to send for compliance signatures.',
        completed: template?.status === 'published'
      }
    ];
  }

  return [
    {
      label: 'Upload your PDF template',
      description: 'Drop a PDF or use the upload button to add the document.',
      completed: Boolean(activeAttachment)
    },
    {
      label: 'Place signer fields',
      description: 'Drag required fields onto each page of the PDF.',
      completed: fields.length > 0
    },
    {
      label: 'Save the layout',
      description: 'Capture field positions and settings before leaving the page.',
      completed: Boolean(activeAttachment) && fields.length > 0 && !hasUnsavedChanges
    },
    {
      label: 'Publish the template',
      description: 'Make the template available to your team once everything looks correct.',
      completed: template?.status === 'published'
    }
  ];
};

const TemplateDesigner = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [templateTypeOverride, setTemplateTypeOverride] = useState(null);
  const [fields, setFields] = useState([]);
  const [activeAttachment, setActiveAttachment] = useState(null);
  const [numPages, setNumPages] = useState(1);
  const [activePage, setActivePage] = useState(1);
  const [pageDimensions, setPageDimensions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [designerMessage, setDesignerMessage] = useState(null);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const signaturePadRef = useRef(null);
  const lastSavedFingerprint = useRef('[]');
  const theme = useTheme();
  const isNavDocked = useMediaQuery(theme.breakpoints.up('sm'));
  const { setLeftPanelContent } = useDesignerUI();

  // Calculate template type and builder mode early to avoid hook dependency issues
  const templateType = templateTypeOverride || inferTemplateType(template);
  const isBuilder = templateType === 'builder';

  // Drag and drop state - needs to be declared early for hook dependencies
  const dragPayloadRef = useRef(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [isPreviewDragOver, setIsPreviewDragOver] = useState(false);

  const serializeFields = useCallback(fieldsToSerialize => JSON.stringify(
    fieldsToSerialize.map(field => {
      const position = field.config?.position || defaultPosition(field.config?.position?.page || 1);
      return {
        id: field.id || null,
        field_key: field.field_key,
        field_type: field.field_type,
        label: field.label,
        placeholder: field.placeholder || '',
        required: field.required,
        sort_order: field.sort_order ?? 0,
        config: {
          ...field.config,
          position
        }
      };
    })
  ), []);

  const fetchTemplate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getFormsTemplate(id);
      if (!data) {
        throw new Error('Template not found');
      }
      const normalizedTemplate = {
        ...data,
        template_type: inferTemplateType(data)
      };
      setTemplate(normalizedTemplate);
      setTemplateTypeOverride(null);
      const schemaFields = normalizedTemplate.schema?.fields || [];
      const normalizedFields = schemaFields.map((field, index) => ({
        ...field,
        id: field.id || field.field_key || `field-${index}`,
        clientId: field.clientId || field.id || `field-${index}`,
        sort_order: field.sort_order ?? index,
        config: {
          ...(field.config || {}),
          position:
            field.config?.position || defaultPosition(field.config?.position?.page || 1)
        }
      }));
      lastSavedFingerprint.current = serializeFields(normalizedFields);
      setFields(normalizedFields);
      setHasUnsavedChanges(false);
      const firstAttachment = normalizedTemplate.schema?.attachments?.[0] || null;
      setActiveAttachment(firstAttachment);
      setActivePage(1);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to load template');
    } finally {
      setLoading(false);
    }
  }, [id, serializeFields]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  useEffect(() => {
    const fingerprint = serializeFields(fields);
    setHasUnsavedChanges(fingerprint !== lastSavedFingerprint.current);
  }, [fields, serializeFields]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages: loadedPages }) => {
      setNumPages(loadedPages);
      if (activePage > loadedPages) {
        setActivePage(1);
      }
    },
    [activePage]
  );

  const handlePageRender = useCallback(pageNumber => page => {
    setPageDimensions(prev => ({
      ...prev,
      [pageNumber]: {
        width: page.width,
        height: page.height
      }
    }));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    onDrop: async acceptedFiles => {
      if (!acceptedFiles || acceptedFiles.length === 0) {
        return;
      }
      setUploading(true);
      setDesignerMessage('PDF uploads are not yet supported in the new Forms API.');
      setTimeout(() => setUploading(false), 800);
    }
  });

  const handleAddField = useCallback(
    (preset, options = {}) => {
      const timestamp = Date.now();
      const key = `${preset.type}_${timestamp}`;
      const clientId = `temp-${key}`;
      const pageForPosition = template?.template_type === 'pdf' ? activePage : 1;
      const basePosition = defaultPosition(pageForPosition);
      const newConfig = {
        position: basePosition,
        ...(preset.options ? { options: preset.options } : {}),
        ...(preset.defaultConfig || {})
      };

      const newField = {
        id: null,
        clientId,
        field_key: key,
        field_type: preset.type,
        label: preset.label,
        placeholder:
          typeof preset.placeholder !== 'undefined'
            ? preset.placeholder
            : preset.type === 'text'
              ? 'Enter response'
              : '',
        required: Boolean(preset.required),
        config: newConfig,
        sort_order: options?.insertIndex ?? 0
      };

      setFields(prev => {
        const insertIndex =
          typeof options?.insertIndex === 'number'
            ? Math.min(Math.max(options.insertIndex, 0), prev.length)
            : prev.length;
        const next = [...prev];
        next.splice(insertIndex, 0, newField);
        return next;
      });
      setSelectedFieldId(clientId);
    },
    [activePage, template]
  );

  const handleRemoveField = useCallback(fieldClientId => {
    setFields(prev => prev.filter(f => f.clientId !== fieldClientId));
    if (selectedFieldId === fieldClientId) {
      setSelectedFieldId(null);
    }
  }, [selectedFieldId]);

  const handleMoveField = useCallback((fieldClientId, direction) => {
    setFields(prev => {
      const index = prev.findIndex(field => field.clientId === fieldClientId);
      if (index === -1) {
        return prev;
      }
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) {
        return prev;
      }
      const updated = [...prev];
      const [moved] = updated.splice(index, 1);
      updated.splice(targetIndex, 0, moved);
      return updated;
    });
  }, []);

  const moveFieldToIndex = useCallback((fieldClientId, targetIndex) => {
    setFields(prev => {
      const currentIndex = prev.findIndex(field => field.clientId === fieldClientId);
      if (currentIndex === -1) {
        return prev;
      }
      const next = [...prev];
      const [moved] = next.splice(currentIndex, 1);
      const clampedIndex = Math.min(Math.max(targetIndex, 0), next.length);
      next.splice(clampedIndex, 0, moved);
      return next;
    });
  }, []);

  const handleDuplicateField = useCallback(fieldClientId => {
    let newClientId = null;
    setFields(prev => {
      const index = prev.findIndex(field => field.clientId === fieldClientId);
      if (index === -1) {
        return prev;
      }
      const original = prev[index];
      const timestamp = Date.now();
      newClientId = `temp-${original.field_type}-${timestamp}`;
      const clonedConfig = JSON.parse(JSON.stringify(original.config || {}));
      const clone = {
        ...original,
        id: null,
        clientId: newClientId,
        field_key: `${original.field_type}_${timestamp}`,
        label: `${original.label} copy`,
        config: clonedConfig
      };
      return [
        ...prev.slice(0, index + 1),
        clone,
        ...prev.slice(index + 1)
      ];
    });
    if (newClientId) {
      setSelectedFieldId(newClientId);
    }
  }, []);

  const handleFieldConfigPatch = useCallback((clientId, patch) => {
    setFields(prev =>
      prev.map(field =>
        field.clientId === clientId
          ? {
              ...field,
              config: {
                ...(field.config || {}),
                position: field.config?.position || defaultPosition(field.config?.position?.page || 1),
                ...patch
              }
            }
          : field
      )
    );
  }, []);

  const resetDragState = useCallback(() => {
    dragPayloadRef.current = null;
    setDropTargetId(null);
    setIsPreviewDragOver(false);
  }, []);

  const handlePaletteDragStart = useCallback(
    (event, preset) => {
      if (!isBuilder) {
        return;
      }
      dragPayloadRef.current = { type: 'preset', preset };
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData('text/plain', preset.type);
    },
    [isBuilder]
  );

  const handleFieldDragStart = useCallback(
    (event, fieldId) => {
      if (!isBuilder) {
        return;
      }
      dragPayloadRef.current = { type: 'field', fieldId };
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', fieldId);
    },
    [isBuilder]
  );

  const handleDragEnd = useCallback(() => {
    if (!isBuilder) {
      return;
    }
    resetDragState();
  }, [isBuilder, resetDragState]);

  const handlePreviewDragOver = useCallback(
    event => {
      if (!isBuilder || !dragPayloadRef.current) {
        return;
      }
      event.preventDefault();
      setIsPreviewDragOver(true);
      setDropTargetId('__end__');
      event.dataTransfer.dropEffect = dragPayloadRef.current.type === 'preset' ? 'copy' : 'move';
    },
    [isBuilder]
  );

  const handlePreviewDragLeave = useCallback(
    event => {
      if (!isBuilder) {
        return;
      }
      if (event.currentTarget.contains(event.relatedTarget)) {
        return;
      }
      setIsPreviewDragOver(false);
      if (dropTargetId === '__end__') {
        setDropTargetId(null);
      }
    },
    [isBuilder, dropTargetId]
  );

  const handlePreviewDrop = useCallback(
    event => {
      if (!isBuilder || !dragPayloadRef.current) {
        return;
      }
      event.preventDefault();
      const payload = dragPayloadRef.current;
      if (payload.type === 'preset') {
        handleAddField(payload.preset, { insertIndex: fields.length });
      } else if (payload.type === 'field') {
        moveFieldToIndex(payload.fieldId, fields.length);
      }
      resetDragState();
    },
    [isBuilder, handleAddField, fields, moveFieldToIndex, resetDragState]
  );

  const handleFieldDragOver = useCallback(
    (event, fieldId) => {
      if (!isBuilder || !dragPayloadRef.current) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setDropTargetId(fieldId);
      event.dataTransfer.dropEffect = dragPayloadRef.current.type === 'preset' ? 'copy' : 'move';
    },
    [isBuilder]
  );

  const handleFieldDragLeave = useCallback(
    (event, fieldId) => {
      if (!isBuilder) {
        return;
      }
      event.stopPropagation();
      if (event.currentTarget && event.currentTarget.contains(event.relatedTarget)) {
        return;
      }
      if (dropTargetId === fieldId) {
        setDropTargetId(null);
      }
    },
    [isBuilder, dropTargetId]
  );

  const handleFieldDrop = useCallback(
    (event, fieldId) => {
      if (!isBuilder || !dragPayloadRef.current) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const payload = dragPayloadRef.current;
      const targetIndex = fields.findIndex(field => field.clientId === fieldId);
      if (targetIndex === -1) {
        return;
      }
      if (payload.type === 'preset') {
        handleAddField(payload.preset, { insertIndex: targetIndex });
      } else if (payload.type === 'field') {
        moveFieldToIndex(payload.fieldId, targetIndex);
      }
      resetDragState();
    },
    [isBuilder, fields, handleAddField, moveFieldToIndex, resetDragState]
  );

  const updateFieldPosition = useCallback((clientId, position) => {
    setFields(prev =>
      prev.map(field =>
        field.clientId === clientId
          ? {
              ...field,
              config: {
                ...(field.config || {}),
                position: {
                  ...(field.config?.position || defaultPosition()),
                  ...position
                }
              }
            }
          : field
      )
    );
  }, []);

  const handleSaveLayout = useCallback(async () => {
    if (!template) return;
    setSaving(true);
    setError(null);
    setDesignerMessage(null);
    try {
      const nextFields = fields.map((field, index) => {
        const position =
          field.config?.position || defaultPosition(field.config?.position?.page || 1);
        const config = {
          ...field.config,
          position
        };
        return {
          id: field.id || undefined,
          field_key: field.field_key,
          field_type: field.field_type,
          label: field.label,
          placeholder: field.placeholder || '',
          required: Boolean(field.required),
          sort_order: index,
          position,
          options: config.options || null,
          config
        };
      });

      const updated = await updateFormsTemplate(template.id, {
        schema: {
          ...(template.schema || {}),
          fields: nextFields
        }
      });

      setTemplate(updated);
      setTemplateTypeOverride(null);
      const normalizedFields = (updated.schema?.fields || []).map((field, index) => ({
        ...field,
        id: field.id || field.field_key || `field-${index}`,
        clientId: field.clientId || field.id || `field-${index}`,
        sort_order: field.sort_order ?? index,
        config: {
          ...(field.config || {}),
          position:
            field.config?.position || defaultPosition(field.config?.position?.page || 1)
        }
      }));
      lastSavedFingerprint.current = serializeFields(normalizedFields);
      setFields(normalizedFields);
      setHasUnsavedChanges(false);
      setDesignerMessage('Layout saved successfully');
    } catch (saveErr) {
      console.error(saveErr);
      setError(saveErr.message || 'Failed to save layout');
    } finally {
      setSaving(false);
    }
  }, [fields, template, serializeFields]);

  const handlePublishTemplate = useCallback(async () => {
    if (!template) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateFormsTemplate(template.id, {
        schema: {
          ...(template.schema || {}),
          status: 'published'
        }
      });

      setTemplate(updated);
      setTemplateTypeOverride(null);
      setDesignerMessage('Template published');
    } catch (publishErr) {
      console.error(publishErr);
      setError(publishErr.message || 'Failed to publish template');
    } finally {
      setSaving(false);
    }
  }, [template]);

  const handleTemplateTypeChange = useCallback(
    async nextType => {
      if (!template || template.template_type === nextType) {
        return;
      }
      setTemplateTypeOverride(nextType);
      setSaving(true);
      setError(null);
      try {
        const updated = await updateFormsTemplate(template.id, {
          schema: {
            ...(template.schema || {}),
            template_type: nextType
          }
        });

        const normalizedTemplate = {
          ...updated,
          template_type: inferTemplateType({ ...updated, template_type: nextType })
        };
        setTemplate(normalizedTemplate);
        setTemplateTypeOverride(null);
        setDesignerMessage(
          nextType === 'builder'
            ? 'Switched to Builder mode. Drag fields in to start designing.'
            : 'Switched to PDF mode.'
        );
        if (nextType === 'builder') {
          setActiveAttachment(null);
        } else {
          const firstAttachment = normalizedTemplate.schema?.attachments?.[0] || null;
          setActiveAttachment(firstAttachment);
        }
        setActivePage(1);
      } catch (typeErr) {
        console.error(typeErr);
        setTemplateTypeOverride(null);
        setError(typeErr.message || 'Failed to change template type');
      } finally {
        setSaving(false);
      }
    },
    [template, inferTemplateType]
  );

  const handleSignaturePreviewSave = useCallback(async () => {
    if (!signaturePadRef.current) {
      return;
    }
    const signatureEmpty = signaturePadRef.current.isEmpty();
    if (signatureEmpty) {
      setDesignerMessage('Draw a signature first');
      return;
    }
    const dataUrl = signaturePadRef.current.toDataURL();
    setDesignerMessage(`Captured signature preview (${dataUrl.length} bytes)`);
  }, []);

  // Render function for builder preview controls
  const renderBuilderPreviewControl = useCallback(field => {
    const options = Array.isArray(field.config?.options) ? field.config.options : [];
    const helpText = field.config?.helpText || '';
    const baseLabel = field.label || (field.field_type === 'title' ? 'Form Title' : 'Field');
    const labelWithRequired = STATIC_FIELD_TYPES.has(field.field_type)
      ? baseLabel
      : `${baseLabel}${field.required ? ' *' : ''}`;

    switch (field.field_type) {
      case 'title': {
        const align = field.config?.align || 'center';
        const headingText = field.config?.headingText || baseLabel;
        const subtitle = field.config?.subtitle || '';
        return (
          <Box
            sx={{
              textAlign: align,
              px: { xs: 1, md: 2 },
              py: 2,
              bgcolor: 'rgba(12,80,133,0.04)',
              borderRadius: 2,
              border: '1px dashed rgba(12,80,133,0.2)'
            }}
          >
            <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {headingText}
            </Typography>
            {subtitle && (
              <Typography variant="subtitle1" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        );
      }
      case 'static_text': {
        const align = field.config?.align || 'left';
        const content = field.config?.content || 'Add your form instructions or policy language here.';
        const paragraphs = content.split('\n');
        return (
          <Box
            sx={{
              textAlign: align,
              px: { xs: 1, md: 2 },
              py: 2,
              bgcolor: 'rgba(12,80,133,0.03)',
              borderRadius: 2,
              border: '1px dashed rgba(12,80,133,0.16)'
            }}
          >
            {paragraphs.map((paragraph, idx) => (
              <Typography
                key={idx}
                variant="body1"
                color="text.primary"
                sx={{ mb: idx === paragraphs.length - 1 ? 0 : 1 }}
              >
                {paragraph}
              </Typography>
            ))}
          </Box>
        );
      }
      case 'sent_date': {
        const format = field.config?.dateFormat || 'MMMM d, yyyy';
        const helper = field.config?.helperText || 'Auto-populated when the template is sent';
        return (
          <Box
            sx={{
              border: '1px dashed rgba(12,80,133,0.2)',
              borderRadius: 2,
              p: 2,
              bgcolor: 'rgba(12,80,133,0.03)'
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {baseLabel}
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
              {format}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {helper}
            </Typography>
          </Box>
        );
      }
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
        return (
          <TextField
            fullWidth
            label={labelWithRequired}
            placeholder={field.placeholder || ''}
            disabled
            type={
              field.field_type === 'phone'
                ? 'tel'
                : field.field_type === 'number'
                  ? 'number'
                  : field.field_type === 'email'
                    ? 'email'
                    : 'text'
            }
            helperText={helpText}
          />
        );
      case 'textarea':
        return (
          <TextField
            fullWidth
            label={labelWithRequired}
            placeholder={field.placeholder || ''}
            disabled
            multiline
            minRows={3}
            helperText={helpText}
          />
        );
      case 'select':
        return (
          <TextField
            select
            fullWidth
            label={labelWithRequired}
            value=""
            disabled
            helperText={helpText || 'Dropdown preview'}
            SelectProps={{ native: true }}
          >
            <option value="">Select an option</option>
            {options.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </TextField>
        );
      case 'checkbox':
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {labelWithRequired}
            </Typography>
            <FormGroup>
              {options.length > 0 ? (
                options.map(option => (
                  <FormControlLabel key={option} control={<Checkbox disabled />} label={option} />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Add options to show choices to signers.
                </Typography>
              )}
            </FormGroup>
            {helpText && (
              <Typography variant="caption" color="text.secondary">
                {helpText}
              </Typography>
            )}
          </Box>
        );
      case 'date':
        return (
          <TextField
            fullWidth
            label={labelWithRequired}
            type="date"
            disabled
            helperText={helpText}
          />
        );
      case 'signature':
        return (
          <Box
            sx={{
              border: '2px dashed rgba(12,80,133,0.4)',
              borderRadius: 1,
              p: 2,
              textAlign: 'center',
              color: 'text.secondary'
            }}
          >
            Sign here
          </Box>
        );
      case 'initials':
        return (
          <Box
            sx={{
              border: '1px dashed rgba(12,80,133,0.4)',
              borderRadius: 1,
              p: 2,
              width: { xs: '100%', sm: '60%', md: '50%' },
              textAlign: 'center',
              color: 'text.secondary'
            }}
          >
            Initials
          </Box>
        );
      default:
        return (
          <TextField fullWidth label={labelWithRequired} disabled helperText={helpText} />
        );
    }
  }, []);

  // Field selection and configuration helpers - needs to be early for component render
  const selectedField = useMemo(
    () => fields.find(field => field.clientId === selectedFieldId),
    [fields, selectedFieldId]
  );

  const selectedFieldSupportsPlaceholder = selectedField ? PLACEHOLDER_FIELD_TYPES.has(selectedField.field_type) : false;
  const selectedFieldHasOptions = selectedField ? OPTION_FIELD_TYPES.has(selectedField.field_type) : false;
  const optionEditorValue = selectedFieldHasOptions ? (selectedField.config?.options || []).join('\n') : '';

  // Available presets for the current template type
  const availablePresets = useMemo(
    () => FIELD_PRESETS.filter(preset => preset.modes.includes(templateType)),
    [templateType]
  );

  // Fields for the current page/mode
  const currentFields = useMemo(() => {
    if (templateType === 'builder') {
      return fields;
    }
    return fields.filter(field => {
      const positionPage = field.config?.position?.page || 1;
      return positionPage === activePage;
    });
  }, [fields, activePage, templateType]);

  useEffect(() => {
    if (!isBuilder) {
      dragPayloadRef.current = null;
      setDropTargetId(null);
      setIsPreviewDragOver(false);
    }
  }, [isBuilder]);

  const stepContext = useMemo(() => ({
    activeAttachment,
    fields,
    hasUnsavedChanges,
    template
  }), [activeAttachment, fields, hasUnsavedChanges, template]);

  const { definitions: stepDefinitions, activeStep, isStepCompleted } = useMemo(
    () => createStepHelper(templateType, stepContext),
    [templateType, stepContext]
  );

  const quickStartItems = useMemo(
    () => createQuickStartItems(templateType, stepContext),
    [templateType, stepContext]
  );

  const renderQuickStartContent = () => (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6">Quick Start</Typography>
        <Tooltip title="Follow these checkpoints to publish a usable template.">
          <ChecklistRtlIcon color="primary" fontSize="small" />
        </Tooltip>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Work from top to bottom—each item lights up when you complete it.
      </Typography>
      <Stack spacing={1.5}>
        {quickStartItems.map(item => (
          <Stack direction="row" spacing={1.5} key={item.label} alignItems="flex-start">
            <CheckCircleOutlineIcon
              fontSize="small"
              sx={{ color: item.completed ? 'success.main' : 'text.disabled', mt: 0.25 }}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {item.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.description}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </>
  );

  const quickStartCard = (
    <Paper elevation={2} sx={{ p: 2 }}>
      {renderQuickStartContent()}
    </Paper>
  );

  const renderSignaturePreviewContent = () => (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6">Signature Capture Preview</Typography>
        <Tooltip title="Demonstrate touch signature capture for clients">
          <TouchAppIcon fontSize="small" color="primary" />
        </Tooltip>
      </Stack>
      <SignatureCapture ref={signaturePadRef} />
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          variant="outlined"
          onClick={() => signaturePadRef.current?.clear()}
        >
          Clear
        </Button>
        <Button variant="contained" onClick={handleSignaturePreviewSave}>
          Save Preview
        </Button>
      </Stack>
    </>
  );

  const signaturePreviewCard = (
    <Paper elevation={2} sx={{ p: 2 }}>
      {renderSignaturePreviewContent()}
    </Paper>
  );

  const renderFieldDetailsContent = () => {
    const isStaticField = selectedField ? STATIC_FIELD_TYPES.has(selectedField.field_type) : false;
    const labelHelperText = selectedField
      ? isStaticField
        ? 'Internal reference name shown in the field list and exports'
        : isBuilder
          ? 'Shown to the signer above the field'
          : 'Shown to the signer inside the document overlay'
      : '';

    const introCopy = selectedField
      ? selectedField.field_type === 'title'
        ? 'Update the heading text and visuals shown at the top of your form.'
        : selectedField.field_type === 'static_text'
          ? 'Compose the narrative or instructional copy that should appear in this section of the document.'
          : selectedField.field_type === 'sent_date'
            ? 'Configure how the automatic send date placeholder should appear.'
            : isBuilder
              ? 'Rename the field, decide if it is required, and add helper text or options for compliance.'
              : 'Rename the field, set whether it is required, and move it to a different page if needed.'
      : '';

    return (
      <>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6">Field Details</Typography>
          <Tooltip title="Update the text and metadata that signers see.">
            <InfoOutlinedIcon color="primary" fontSize="small" />
          </Tooltip>
        </Stack>
        {selectedField ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {introCopy}
            </Typography>
            <Stack spacing={2}>
              <TextField
                label={isStaticField ? 'Internal Name' : 'Label'}
                value={selectedField.label}
                onChange={event => handleFieldConfigChange(selectedField.clientId, 'label', event.target.value)}
                helperText={labelHelperText}
              />
              {selectedField.field_type === 'title' && (
                <>
                  <TextField
                    label="Heading Text"
                    value={selectedField.config?.headingText ?? selectedField.label}
                    onChange={event =>
                      handleFieldConfigPatch(selectedField.clientId, {
                        headingText: event.target.value
                      })
                    }
                    helperText="Displayed as the primary heading for this template"
                  />
                  <TextField
                    label="Subtitle (optional)"
                    value={selectedField.config?.subtitle || ''}
                    onChange={event =>
                      handleFieldConfigPatch(selectedField.clientId, {
                        subtitle: event.target.value
                      })
                    }
                    helperText="Shown beneath the heading to provide extra context"
                  />
                  <TextField
                    select
                    label="Alignment"
                    value={selectedField.config?.align || 'center'}
                    onChange={event =>
                      handleFieldConfigPatch(selectedField.clientId, {
                        align: event.target.value
                      })
                    }
                    helperText="Choose how the heading should be aligned"
                  >
                    <MenuItem value="left">Left</MenuItem>
                    <MenuItem value="center">Center</MenuItem>
                    <MenuItem value="right">Right</MenuItem>
                  </TextField>
                </>
              )}
              {selectedField.field_type === 'static_text' && (
                <>
                  <TextField
                    label="Content"
                    value={selectedField.config?.content || ''}
                    onChange={event =>
                      handleFieldConfigPatch(selectedField.clientId, {
                        content: event.target.value
                      })
                    }
                    helperText="Write the wording that should always appear on this form. Use Enter to create new paragraphs."
                    multiline
                    minRows={4}
                  />
                  <TextField
                    select
                    label="Alignment"
                    value={selectedField.config?.align || 'left'}
                    onChange={event =>
                      handleFieldConfigPatch(selectedField.clientId, {
                        align: event.target.value
                      })
                    }
                    helperText="Control whether the text block is left, center, or right aligned"
                  >
                    <MenuItem value="left">Left</MenuItem>
                    <MenuItem value="center">Center</MenuItem>
                    <MenuItem value="right">Right</MenuItem>
                  </TextField>
                </>
              )}
              {selectedFieldSupportsPlaceholder && (
                <TextField
                  label="Placeholder"
                  value={selectedField.placeholder || ''}
                  onChange={event => handleFieldConfigChange(selectedField.clientId, 'placeholder', event.target.value)}
                  helperText={isBuilder ? 'Optional hint text displayed before the signer types' : 'Optional hint text displayed before the signer types'}
                />
              )}
              <TextField
                label="Field Key"
                value={selectedField.field_key}
                onChange={event => handleFieldConfigChange(selectedField.clientId, 'field_key', event.target.value)}
                helperText="Unique identifier used in exports and audit logs"
              />
              {!isStaticField && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(selectedField.required)}
                      onChange={event =>
                        handleFieldConfigChange(selectedField.clientId, 'required', event.target.checked)
                      }
                    />
                  }
                  label="Required field"
                />
              )}
              {selectedField.field_type === 'sent_date' && (
                <>
                  <TextField
                    label="Display Format"
                    value={selectedField.config?.dateFormat || 'MMMM d, yyyy'}
                    onChange={event =>
                      handleFieldConfigPatch(selectedField.clientId, {
                        dateFormat: event.target.value
                      })
                    }
                    helperText="Uses date-fns format tokens (e.g. MMMM d, yyyy or MM/dd/yyyy)"
                  />
                  <TextField
                    label="Helper Text"
                    value={selectedField.config?.helperText || 'Auto-populated when the template is sent'}
                    onChange={event =>
                      handleFieldConfigPatch(selectedField.clientId, {
                        helperText: event.target.value
                      })
                    }
                    helperText="Shown beneath the placeholder to remind signers how it behaves"
                  />
                </>
              )}
              {selectedField.field_type === 'static_text' && (
                <Typography variant="caption" color="text.secondary">
                  Use this block for policies, onboarding checklists, or disciplinary notes so every recipient sees the same language.
                </Typography>
              )}
              {isBuilder && selectedFieldHasOptions && (
                <TextField
                  label="Options"
                  value={optionEditorValue}
                  onChange={event => {
                    const lines = event.target.value
                      .split('\n')
                      .map(option => option.trim())
                      .filter(Boolean);
                    handleFieldConfigPatch(selectedField.clientId, {
                      options: lines.length > 0 ? lines : []
                    });
                  }}
                  helperText="One option per line. These choices appear in the form."
                  multiline
                  minRows={3}
                />
              )}
              {isBuilder && !isStaticField && (
                <TextField
                  label="Help Text"
                  value={selectedField.config?.helpText || ''}
                  onChange={event =>
                    handleFieldConfigPatch(selectedField.clientId, {
                      helpText: event.target.value
                    })
                  }
                  helperText="Optional guidance shown under the field"
                />
              )}
              {selectedField.field_type === 'sent_date' && (
                <Typography variant="caption" color="text.secondary">
                  The send date is inserted automatically when this template is dispatched—signers cannot edit it.
                </Typography>
              )}
              {templateType === 'pdf' && (
                <TextField
                  label="Page"
                  type="number"
                  inputProps={{ min: 1, max: numPages }}
                  value={selectedField.config?.position?.page || activePage}
                  onChange={event => {
                    const newPage = Math.min(Math.max(Number(event.target.value), 1), numPages);
                    updateFieldPosition(selectedField.clientId, {
                      page: newPage
                    });
                  }}
                  helperText="Switch pages if this field belongs elsewhere in the document"
                />
              )}
            </Stack>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Select a field to edit its settings.
          </Typography>
        )}
      </>
    );
  };

  const fieldDetailsCard = (
    <Paper elevation={2} sx={{ p: 2 }}>
      {renderFieldDetailsContent()}
    </Paper>
  );

  const renderBuilderPaletteContent = useCallback(() => (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6">Field Palette</Typography>
        <Tooltip title="Drag a field into the preview or click to add it below.">
          <AutoAwesomeIcon color="primary" fontSize="small" />
        </Tooltip>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drag or click any field type to add it to your form. Use the live preview to arrange the order.
      </Typography>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        {availablePresets.map(preset => {
          const IconComponent = preset.icon;
          return (
            <Tooltip key={preset.type} title={preset.description} arrow>
              <Paper
                component="button"
                type="button"
                onClick={() => handleAddField(preset)}
                draggable
                onDragStart={event => handlePaletteDragStart(event, preset)}
                onDragEnd={handleDragEnd}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  minWidth: 200,
                  textAlign: 'left',
                  cursor: 'grab',
                  backgroundColor: 'background.paper',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 3px rgba(12,80,133,0.1)'
                  },
                  '&:active': {
                    cursor: 'grabbing'
                  },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '2px'
                  }
                }}
              >
                <IconComponent fontSize="small" color="primary" />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {preset.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Drag or click to add
                  </Typography>
                </Box>
                <Box sx={{ marginLeft: 'auto' }}>
                  <AddIcon fontSize="small" color="action" />
                </Box>
              </Paper>
            </Tooltip>
          );
        })}
      </Stack>
    </>
  ), [availablePresets, handleAddField, handlePaletteDragStart, handleDragEnd]);

  const builderPaletteCard = (
    <Paper elevation={2} sx={{ p: 2 }}>
      {renderBuilderPaletteContent()}
    </Paper>
  );

  const navPaletteContent = useMemo(
    () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {renderBuilderPaletteContent()}
      </Box>
    ),
    [renderBuilderPaletteContent]
  );

  useEffect(() => {
    if (!isBuilder) {
      setLeftPanelContent(null);
      return;
    }
    setLeftPanelContent(navPaletteContent);
    return () => setLeftPanelContent(null);
  }, [isBuilder, navPaletteContent, setLeftPanelContent]);

  const builderPreviewCard = (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: { xs: 'auto', lg: '70vh' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        width: '100%',
        maxWidth: 960,
        mx: 'auto',
        position: 'relative'
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6">Live Preview</Typography>
        <Tooltip title="Drag cards to reorder or drop palette items in between.">
          <InfoOutlinedIcon color="primary" fontSize="small" />
        </Tooltip>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drag fields into this preview to design the signer experience. Drop above or below a card to adjust the order.
      </Typography>
      <Box
        onDragOver={handlePreviewDragOver}
        onDragLeave={handlePreviewDragLeave}
        onDrop={handlePreviewDrop}
        sx={{
          border: '2px dashed',
          borderColor: dropTargetId === '__end__' || isPreviewDragOver ? 'primary.main' : 'rgba(12,80,133,0.2)',
          borderRadius: 2,
          p: fields.length === 0 ? { xs: 4, lg: 6 } : { xs: 2, lg: 3 },
          minHeight: fields.length === 0 ? { xs: 180, lg: 320 } : { xs: 160, lg: 280 },
          bgcolor: dropTargetId === '__end__' || isPreviewDragOver ? 'rgba(12,80,133,0.05)' : 'transparent',
          transition: 'all 0.2s ease',
          flexGrow: 1
        }}
      >
        {fields.length === 0 ? (
          <Stack alignItems="center" spacing={1.5}>
            <Typography variant="body1" color="text.secondary">
              Drag a field here or click a palette item to start building your form.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              You can rearrange blocks at any time by dragging them up or down.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {fields.map((field, index) => {
              const isSelected = selectedFieldId === field.clientId;
              const isDropTarget = dropTargetId === field.clientId;
              const previewLabel = field.field_type === 'title'
                ? field.config?.headingText || field.label
                : field.field_type === 'static_text'
                  ? (field.config?.content || field.label || '')
                      .split('\n')[0]
                      .slice(0, 48)
                      .concat(((field.config?.content || '').length > 48) ? '…' : '')
                : field.field_type === 'sent_date'
                  ? `${field.label} (${field.config?.dateFormat || 'MMMM d, yyyy'})`
                  : field.label;
              const chipColor = field.required
                ? 'primary'
                : STATIC_FIELD_TYPES.has(field.field_type)
                  ? 'info'
                  : 'default';
              return (
                <Paper
                  key={field.clientId}
                  variant="outlined"
                  draggable
                  onDragStart={event => handleFieldDragStart(event, field.clientId)}
                  onDragEnd={handleDragEnd}
                  onDragOver={event => handleFieldDragOver(event, field.clientId)}
                  onDragLeave={event => handleFieldDragLeave(event, field.clientId)}
                  onDrop={event => handleFieldDrop(event, field.clientId)}
                  sx={{
                    p: 1.5,
                    borderColor: isDropTarget
                      ? 'primary.main'
                      : isSelected
                        ? 'primary.main'
                        : 'rgba(12,80,133,0.2)',
                    borderStyle: isDropTarget ? 'dashed' : 'solid',
                    boxShadow: isDropTarget ? '0 0 0 3px rgba(12,80,133,0.12)' : undefined,
                    cursor: isDropTarget ? 'grabbing' : 'grab',
                    backgroundColor: isSelected ? 'rgba(12,80,133,0.05)' : 'background.paper',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease'
                  }}
                  onClick={() => setSelectedFieldId(field.clientId)}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip
                        label={field.field_type.toUpperCase()}
                        size="small"
                        color={chipColor}
                      />
                      <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 600 }}>
                        {previewLabel}
                      </Typography>
                      <Tooltip title="Move up">
                        <span>
                          <IconButton
                            size="small"
                            onClick={event => {
                              event.stopPropagation();
                              handleMoveField(field.clientId, 'up');
                            }}
                            disabled={index === 0}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <span>
                          <IconButton
                            size="small"
                            onClick={event => {
                              event.stopPropagation();
                              handleMoveField(field.clientId, 'down');
                            }}
                            disabled={index === fields.length - 1}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Duplicate field">
                        <IconButton
                          size="small"
                          onClick={event => {
                            event.stopPropagation();
                            handleDuplicateField(field.clientId);
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove field">
                        <IconButton
                          size="small"
                          onClick={event => {
                            event.stopPropagation();
                            handleRemoveField(field.clientId);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {renderBuilderPreviewControl(field)}
                    <Typography variant="caption" color="text.secondary">
                      key: {field.field_key} · {STATIC_FIELD_TYPES.has(field.field_type) ? 'static' : field.required ? 'required' : 'optional'}
                    </Typography>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>
      {fields.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Tip: Drag palette items between cards to insert a new field exactly where you need it.
        </Typography>
      )}
    </Paper>
  );

  const pdfFieldPaletteCard = (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6">Field Palette</Typography>
        <Tooltip title="Quick actions to drop signer inputs on the page.">
          <AutoAwesomeIcon color="primary" fontSize="small" />
        </Tooltip>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Click any field type to add it to page {activePage}, then drag and resize the overlay on the document preview.
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {availablePresets.map(preset => {
          const IconComponent = preset.icon;
          return (
            <Tooltip key={preset.type} title={preset.description} arrow>
              <Button
                variant="outlined"
                startIcon={<IconComponent fontSize="small" />}
                endIcon={<AddIcon fontSize="small" />}
                onClick={() => handleAddField(preset)}
                sx={{ minWidth: 180, justifyContent: 'space-between' }}
              >
                {preset.label}
              </Button>
            </Tooltip>
          );
        })}
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" gutterBottom>
        Fields on Page {activePage}
      </Typography>
      <Stack spacing={1}>
        {currentFields.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No fields on this page yet.
          </Typography>
        )}
        {currentFields.map(field => (
          <Paper
            key={field.clientId}
            variant="outlined"
            sx={{
              p: 1.5,
              borderColor: selectedFieldId === field.clientId ? 'primary.main' : undefined,
              cursor: 'pointer'
            }}
            onClick={() => setSelectedFieldId(field.clientId)}
          >
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip
                  label={field.field_type.toUpperCase()}
                  size="small"
                  color={field.required ? 'primary' : 'default'}
                />
                <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 600 }}>
                  {field.label}
                </Typography>
                <IconButton
                  size="small"
                  onClick={event => {
                    event.stopPropagation();
                    handleRemoveField(field.clientId);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                key: {field.field_key} · {field.required ? 'required' : 'optional'}
              </Typography>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Paper>
  );

  const handleFieldConfigChange = (clientId, key, value) => {
    setFields(prev =>
      prev.map(field =>
        field.clientId === clientId
          ? {
              ...field,
              [key]: value
            }
          : field
      )
    );
  };

  const renderFieldOverlays = (pageWidth, pageHeight) =>
    currentFields.map(field => {
      const position = field.config?.position || defaultPosition(activePage);
      const left = position.x * pageWidth;
      const top = position.y * pageHeight;
      const width = position.width * pageWidth;
      const height = position.height * pageHeight;
      const isSelected = selectedFieldId === field.clientId;
      const isTitle = field.field_type === 'title';
      const isSentDate = field.field_type === 'sent_date';
      const isStaticText = field.field_type === 'static_text';
      const headingAlign = field.config?.align || 'center';
      const textAlign = isStaticText ? field.config?.align || 'left' : headingAlign;
      const displayText = isTitle
        ? field.config?.headingText || field.label || 'Form Title'
        : isSentDate
          ? `${field.label || 'Send Date'} (${field.config?.dateFormat || 'MMMM d, yyyy'})`
          : isStaticText
            ? field.config?.content || field.label || ''
            : field.label;

      return (
        <Rnd
          key={field.clientId}
          bounds="parent"
          size={{ width, height }}
          position={{ x: left, y: top }}
          onDragStop={(e, data) => {
            updateFieldPosition(field.clientId, {
              x: data.x / pageWidth,
              y: data.y / pageHeight
            });
          }}
          onResizeStop={(e, direction, ref, delta, positionData) => {
            updateFieldPosition(field.clientId, {
              x: positionData.x / pageWidth,
              y: positionData.y / pageHeight,
              width: ref.offsetWidth / pageWidth,
              height: ref.offsetHeight / pageHeight
            });
          }}
          minWidth={isTitle ? 140 : isStaticText ? 160 : 60}
          minHeight={isTitle ? 48 : isStaticText ? 56 : 32}
        >
          <Tooltip title="Drag to move. Use the resize handles to adjust size." arrow>
            <Box
              onClick={() => setSelectedFieldId(field.clientId)}
              sx={{
                width: '100%',
                height: '100%',
                border: `2px solid ${isSelected ? '#0C5085' : 'rgba(12, 80, 133, 0.5)'}`,
                borderRadius: 1,
                bgcolor: isTitle ? 'rgba(12, 80, 133, 0.12)' : 'rgba(12, 80, 133, 0.08)',
                color: '#0C5085',
                fontSize: isTitle ? 16 : isSentDate ? 13 : isStaticText ? 13 : 12,
                fontWeight: isTitle ? 600 : isStaticText ? 400 : 500,
                display: 'flex',
                alignItems: isStaticText ? 'flex-start' : 'center',
                justifyContent: isTitle
                  ? headingAlign === 'left'
                    ? 'flex-start'
                    : headingAlign === 'right'
                      ? 'flex-end'
                      : 'center'
                  : isStaticText
                    ? 'flex-start'
                    : 'center',
                textAlign: isTitle || isStaticText ? textAlign : 'center',
                px: isTitle || isStaticText ? 2 : 1,
                position: 'relative',
                cursor: 'move'
              }}
            >
              {isStaticText ? (
                <Box sx={{ width: '100%' }}>
                  {displayText.split('\n').map((line, idx, arr) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        mb: idx === arr.length - 1 ? 0 : 0.5,
                        fontWeight: 400,
                        display: 'block'
                      }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>
              ) : (
                displayText
              )}
              <IconButton
                size="small"
                onClick={event => {
                  event.stopPropagation();
                  handleRemoveField(field.clientId);
                }}
                sx={{
                  position: 'absolute',
                  top: -12,
                  right: -12,
                  bgcolor: 'white'
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        </Rnd>
      );
    });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchTemplate}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!template) {
    return null;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1440, mx: 'auto' }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <RouterLink to="/signing">Document Signing</RouterLink>
        <Typography color="text.primary">{template.name}</Typography>
      </Breadcrumbs>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">{template.name}</Typography>
        <Chip label={template.status.toUpperCase()} color={template.status === 'published' ? 'success' : 'default'} />
        <Chip label={templateType.toUpperCase()} color={templateType === 'builder' ? 'info' : 'default'} variant="outlined" />
        <Box sx={{ flexGrow: 1 }} />
        {templateType === 'pdf' && (
          <Button
            variant="text"
            onClick={() => handleTemplateTypeChange('builder')}
            disabled={saving}
          >
            Switch to Builder Mode
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<PublishIcon />}
          onClick={handlePublishTemplate}
          disabled={saving || template.status === 'published'}
        >
          Publish
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveLayout}
          disabled={saving}
        >
          Save Layout
        </Button>
      </Stack>

      <Paper elevation={1} sx={{ mb: 2, p: { xs: 2, md: 3 } }}>
        <Stepper alternativeLabel activeStep={activeStep}>
          {stepDefinitions.map((step, index) => {
            const helper = step.helper;
            return (
              <Step key={step.label} completed={isStepCompleted(index)}>
                <StepLabel
                  optional={
                    helper ? (
                      <Typography variant="caption" color="text.secondary">
                        {helper}
                      </Typography>
                    ) : undefined
                  }
                >
                  {step.label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Paper>

      {hasUnsavedChanges && (
        <Alert
          severity="warning"
          icon={<InfoOutlinedIcon fontSize="inherit" />}
          sx={{ mb: 2 }}
        >
          You have unsaved field changes. Click <strong>Save Layout</strong> before leaving the designer.
        </Alert>
      )}

      {designerMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {designerMessage}
        </Alert>
      )}

      {templateType === 'pdf' && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => handleTemplateTypeChange('builder')}
              disabled={saving}
            >
              Switch to Builder
            </Button>
          }
        >
          This template currently uses a PDF background. Switch to Builder mode to design a drag-and-drop form from scratch.
        </Alert>
      )}

      {isBuilder ? (
        <>
          {!isNavDocked && (
            <Box sx={{ mb: 3 }}>
              {builderPaletteCard}
            </Box>
          )}
          <GridLayout templateType={templateType}>
            <Box>
              {builderPreviewCard}
            </Box>
            <Stack spacing={2}>
              {fieldDetailsCard}
              {quickStartCard}
              {signaturePreviewCard}
            </Stack>
          </GridLayout>
        </>
      ) : (
        <GridLayout templateType={templateType}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6">Template Document</Typography>
              <Chip
                label={`${numPages} page${numPages === 1 ? '' : 's'}`}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`${fields.length} field${fields.length === 1 ? '' : 's'}`}
                size="small"
                color={fields.length > 0 ? 'primary' : 'default'}
                variant={fields.length > 0 ? 'outlined' : 'filled'}
              />
              <Chip
                label={hasUnsavedChanges ? 'Unsaved edits' : 'Layout saved'}
                size="small"
                color={hasUnsavedChanges ? 'warning' : 'success'}
                variant={hasUnsavedChanges ? 'filled' : 'outlined'}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a PDF, switch between pages, then drag any field to reposition it. Resize handles appear when a field is selected.
            </Typography>
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed rgba(12,80,133,0.4)',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                mb: 2,
                cursor: 'pointer',
                bgcolor: isDragActive ? 'rgba(12,80,133,0.08)' : 'transparent'
              }}
            >
              <input {...getInputProps()} />
              <UploadFileIcon fontSize="large" color="primary" />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Drag & drop a PDF or click to upload
              </Typography>
              {uploading && <CircularProgress size={20} sx={{ mt: 1 }} />}
            </Box>

            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
              {(template.schema?.attachments || []).map(attachment => (
                <Chip
                  key={attachment.id}
                  label={attachment.file_name}
                  color={activeAttachment?.id === attachment.id ? 'primary' : 'default'}
                  onClick={() => {
                    setActiveAttachment(attachment);
                    setActivePage(1);
                  }}
                />
              ))}
              {(template.schema?.attachments?.length ?? 0) === 0 && (
                <Typography variant="body2" color="text.secondary">
                  PDF uploads are not yet supported in this environment.
                </Typography>
              )}
            </Stack>

            {activeAttachment ? (
              <Box sx={{ border: '1px solid rgba(12,80,133,0.1)', borderRadius: 2, p: 2 }}>
                <Tabs
                  value={activePage - 1}
                  onChange={(_, value) => setActivePage(value + 1)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 2 }}
                >
                  {Array.from({ length: numPages }, (_, index) => (
                    <Tab key={index} label={`Page ${index + 1}`} />
                  ))}
                </Tabs>
                <Box
                  sx={{
                    position: 'relative',
                    mx: 'auto',
                    maxWidth: 800
                  }}
                >
                  <Document
                    file={`/uploads/${activeAttachment.storage_path}`}
                    loading={
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    }
                    onLoadSuccess={onDocumentLoadSuccess}
                  >
                    <Page
                      pageNumber={activePage}
                      width={800}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      onRenderSuccess={handlePageRender(activePage)}
                    />
                  </Document>
                  {pageDimensions[activePage] && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0
                      }}
                    >
                      {renderFieldOverlays(pageDimensions[activePage].width, pageDimensions[activePage].height)}
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              <Alert severity="info">Upload a PDF template to begin placing fields.</Alert>
            )}
          </Paper>

          <Stack spacing={2}>
            {quickStartCard}
            {pdfFieldPaletteCard}
            {fieldDetailsCard}
            {signaturePreviewCard}
          </Stack>
        </GridLayout>
      )}
    </Box>
  );
};

const GridLayout = ({ children, templateType }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns:
        templateType === 'builder'
          ? { xs: '1fr', lg: 'minmax(0, 2.2fr) minmax(0, 1fr)' }
          : { xs: '1fr', lg: 'minmax(0, 2fr) minmax(0, 1fr)' },
      gap: 3,
      alignItems: 'flex-start'
    }}
  >
    {children}
  </Box>
);

export default TemplateDesigner;
